import Container from "./Container";
import { Facebook, Instagram, Phone, Mail } from "lucide-react";
import { FaTiktok, FaWhatsapp } from "react-icons/fa";
import { StoreSettings } from "../types";
import { Link } from "@inertiajs/react";

const Footer = ({ store_settings }: { store_settings: StoreSettings }) => (
  <footer className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white mt-20 overflow-hidden">
    {/* Sophisticated gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
    
    {/* Elegant top accent - minimal brand presence */}
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-neutral-600 to-transparent"></div>
    <div className="absolute -top-px left-1/3 w-24 h-px bg-gradient-to-r from-brand-primary to-brand-secondary opacity-60 blur-sm"></div>
    <div className="absolute -top-px right-1/3 w-20 h-px bg-gradient-to-r from-brand-secondary to-brand-primary opacity-40 blur-sm"></div>

    <Container className="relative z-10 py-16 px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center md:text-left">
        {/* Brand Column */}
        <div className="animate-fadeInUp md:col-span-2 lg:col-span-1">
             <h3 className="text-xl font-semibold mb-6 text-white flex items-center justify-center md:justify-start">
                 {store_settings?.store_logo && (
                     <img src={store_settings.store_logo} alt={store_settings.store_name} className="h-8 w-auto mr-3" />
                 )}
                 <div className="flex flex-col items-center md:items-start">
                   <span>{store_settings.store_name}</span>
                   {store_settings?.store_slogan && (
                     <span className="text-sm text-neutral-400 italic font-light mt-1">{store_settings.store_slogan}</span>
                   )}
                 </div>
             </h3>
             <p className="text-neutral-400 text-sm">{store_settings.about_text}</p>
        </div>

        {/* Navigation */}
        <div className="animate-fadeInUp">
          <h3 className="text-xl font-semibold mb-6 text-white flex items-center justify-center md:justify-start">
            <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 opacity-60"></div>
            Navegación
          </h3>
          <ul className="space-y-3">
            <li>
              <Link 
                href="/" 
                className="text-neutral-300 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group"
              >
                <span className="border-b border-transparent group-hover:border-neutral-400 transition-all duration-300">
                  Inicio
                </span>
              </Link>
            </li>
            <li>
              <Link 
                href="/about" 
                className="text-neutral-300 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group"
              >
                <span className="border-b border-transparent group-hover:border-neutral-400 transition-all duration-300">
                  Nosotros
                </span>
              </Link>
            </li>
            <li>
              <Link 
                href="/products" 
                className="text-neutral-300 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group"
              >
                <span className="border-b border-transparent group-hover:border-neutral-400 transition-all duration-300">
                  Productos
                </span>
              </Link>
            </li>
            <li>
              <Link 
                href="/blog" 
                className="text-neutral-300 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group"
              >
                <span className="border-b border-transparent group-hover:border-neutral-400 transition-all duration-300">
                  Blog
                </span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact & Support */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-xl font-semibold mb-6 text-white flex items-center justify-center md:justify-start">
            <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 opacity-60"></div>
            Contacto y Soporte
          </h3>
          <ul className="space-y-4">
             {store_settings?.contact_phone && (
               <li className="flex items-center justify-center md:justify-start group hover:text-white transition-colors duration-300">
                 <div className="bg-neutral-800 p-2.5 rounded-xl mr-3 group-hover:bg-neutral-700 transition-colors duration-300 border border-neutral-700">
                   <Phone className="h-5 w-5 text-neutral-400 group-hover:text-white" />
                 </div>
                 <span className="text-neutral-300 group-hover:text-white transition-colors duration-300">{store_settings.contact_phone}</span>
               </li>
             )}
             {store_settings?.whatsapp_number && (
              <li className="flex items-center justify-center md:justify-start group hover:text-green-400 transition-colors duration-300">
                <div className="bg-green-900/30 p-2.5 rounded-xl mr-3 group-hover:bg-green-900/50 transition-colors duration-300 border border-green-800/50">
                  <FaWhatsapp className="h-5 w-5 text-green-400" />
                </div>
                <a
                  href={`https://wa.me/${store_settings.whatsapp_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 group-hover:text-green-400 transition-colors duration-300 hover:underline"
                >
                  {store_settings.whatsapp_number}
                </a>
              </li>
             )}
             {store_settings?.store_email && (
               <li className="flex items-center justify-center md:justify-start group hover:text-blue-400 transition-colors duration-300">
                 <div className="bg-blue-900/30 p-2.5 rounded-xl mr-3 group-hover:bg-blue-900/50 transition-colors duration-300 border border-blue-800/50">
                   <Mail className="h-5 w-5 text-blue-400" />
                 </div>
                 <a
                   href={`mailto:${store_settings.store_email}`}
                   className="text-neutral-300 group-hover:text-blue-400 transition-colors duration-300 hover:underline"
                 >
                   {store_settings.store_email}
                 </a>
               </li>
             )}
             <li>
              <Link
                href="/complaints/new"
                className="text-neutral-300 hover:text-white transition-all duration-300 hover:translate-x-1 inline-block group"
              >
                <span className="border-b border-transparent group-hover:border-neutral-400 transition-all duration-300">
                  Libro de Reclamaciones
                </span>
              </Link>
            </li>
          </ul>
        </div>

        {/* Social Media */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <h3 className="text-xl font-semibold mb-6 text-white flex items-center justify-center md:justify-start">
            <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 opacity-60"></div>
            Síguenos
          </h3>
          <div className="flex justify-center md:justify-start space-x-4 mb-8">
            {store_settings?.social_facebook && (
              <a
                href={store_settings.social_facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="group p-3 bg-neutral-800 rounded-xl hover:bg-blue-600/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg border border-neutral-700 hover:border-blue-600/30"
              >
                <Facebook className="h-6 w-6 text-neutral-400 group-hover:text-blue-400 transition-colors duration-300" />
              </a>
            )}
            {store_settings?.social_instagram && (
              <a
                href={store_settings.social_instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="group p-3 bg-neutral-800 rounded-xl hover:bg-pink-600/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg border border-neutral-700 hover:border-pink-600/30"
              >
                <Instagram className="h-6 w-6 text-neutral-400 group-hover:text-pink-400 transition-colors duration-300" />
              </a>
            )}
            {store_settings?.social_tiktok && (
              <a
                href={store_settings.social_tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Tiktok"
                className="group p-3 bg-neutral-800 rounded-xl hover:bg-neutral-600/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg border border-neutral-700 hover:border-neutral-500"
              >
                <FaTiktok className="h-6 w-6 text-neutral-400 group-hover:text-white transition-colors duration-300" />
              </a>
            )}
          </div>
          
          {/* Newsletter signup hint - refined */}
          <div className="p-4 bg-neutral-800/50 rounded-xl border border-neutral-700/50 backdrop-blur-sm">
            <p className="text-sm text-neutral-300 mb-1">¡Mantente actualizado!</p>
            <p className="text-xs text-neutral-500">Síguenos en redes sociales para ofertas exclusivas</p>
          </div>
        </div>
      </div>

      {/* Elegant divider */}
      <div className="relative mt-12 mb-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-600 to-transparent"></div>
        </div>
        <div className="relative flex justify-center">
          <div className="bg-neutral-800 px-4 py-2 rounded-full border border-neutral-700">
            <div className="w-2 h-2 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full opacity-60"></div>
          </div>
        </div>
      </div>

      {/* Bottom section with sophisticated typography */}
      <div className="text-center space-y-4 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
        <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
          <p className="text-neutral-400 text-sm">
            &copy; {new Date().getFullYear()} {store_settings?.store_name}. Todos los derechos reservados.
          </p>
          <div className="flex items-center space-x-2 text-xs text-neutral-500">
            <span>Powered by</span>
            <span className="text-brand-gradient font-semibold">Dember Solution</span>
          </div>
        </div>
        
        {/* Minimal brand accent */}
        <div className="flex justify-center space-x-1 mt-6 opacity-40">
          <div className="w-1 h-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full animate-pulse"></div>
          <div className="w-1 h-1 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="w-1 h-1 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
    </Container>
  </footer>
);

export default Footer;
